import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_inbound_custom_rbls'

const URLS = {
  BRBL: 'https://campus.barracuda.com/doc/167976621/'
}

function InboundCustomRblsI18n({ classes, productName }: { classes: ReturnType<typeof styles>; productName?: string }) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatValues = { productName }

  return (
    <div className={classes.content} data-testid="content">
      {formatMessage('note', formatValues)}
      <br />
      <br />
      {formatMessage('intro_prefix')}
      <a href={URLS.BRBL} target="_new">
        {formatMessage('intro_link')}
      </a>
      {formatMessage('intro_suffix', formatValues)}
      <p>{formatMessage('subscription_note', formatValues)}</p>
      <p>{formatMessage('table_intro')}</p>
      <ul>
        <li>{formatMessage('columns.rbl_zone')}</li>
        <li>{formatMessage('columns.comment')}</li>
      </ul>
      <br />
      <br />
      {formatMessage('warning')}
    </div>
  )
}

function InboundCustomRbls(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <InboundCustomRblsI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <b>Note</b>: These settings apply to all domains you have verified in {props.productName} for processing email,
      unless you change these settings for a specific domain. In that case, the domain-level settings override the
      global system settings.
      <br />
      <br />
      External IP block lists, also known as DNSBLs or RBLs, are lists of Internet addresses that have been identified
      as potential originators of spam. External IP block lists are efficient and an important part of the spam blocking
      process. In conjunction with the{' '}
      <a href="https://campus.barracuda.com/doc/167976621/" target="_new">
        Barracuda Reputation Block List (BRBL)
      </a>
      , {props.productName} uses these lists to verify the authenticity of the messages you receive. IP Reputation
      checks are only performed on inbound email traffic. All allow lists override external RBLs.
      <p>
        Several organizations maintain external block lists; if you are using a paid or free external block list you can
        leverage the block list if you are within the terms of service. Adding a free RBL service will often not work
        when added to {props.productName}. A paid subscription to a RBL service is required to use their service. This
        subscription will provide you with your own custom RBL hostname that you can add to {props.productName}.
      </p>
      <p>
        Add any additional free or subscription block lists. The table in this page can be sorted by clicking on one of
        the following column titles:
      </p>
      <ul>
        <li>RBL Zone</li>
        <li>Comment</li>
      </ul>
      <br />
      <br />
      <b>Warning:</b>
      Use of any block list may occasionally generate &quot;false positives&quot; (legitimate messages that are
      blocked). Messages blocked due to external block lists or the BRBL are the only blocked messages that are not sent
      to the user&apos;s Message Log.
    </div>
  )
}

export default InboundCustomRbls
